export interface UserMinimal {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface PackageSummary {
  id: string;
  package_id: string;
  package_number: string;
  title: string;
  discipline: string;
  status: 'OPEN' | 'CLOSED' | 'ARCHIVED';
}

export interface TradeReference {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

export interface CostCodeReference {
  id: string;
  code: string;
  name: string;
}

export type SubmittalStatus =
  | 'DRAFT'
  | 'OPEN'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'APPROVED_WITH_COMMENTS'
  | 'REVISE_RESUBMIT'
  | 'REJECTED'
  | 'CLOSED';

export type SubmittalPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type SubmittalType =
  | 'SHOP_DRAWING'
  | 'PRODUCT_DATA'
  | 'SAMPLE'
  | 'CALCULATION'
  | 'BENCHMARK_DOCUMENT'
  | 'WARRANTY'
  | 'O_AND_M_MANUAL'
  | 'OTHER';

export type SubmittalResponseCode =
  | 'FOR_RECORD_ONLY'
  | 'APPROVED'
  | 'APPROVED_AS_NOTED'
  | 'APPROVED_WITH_COMMENTS'
  | 'REVISE_RESUBMIT'
  | 'REJECTED'
  | 'VOID';

export interface SubmittalAttachment {
  id: string;
  submittal: string;
  file: string;
  file_url: string | null;
  uploaded_by: UserMinimal | null;
  created_at: string;
}

export interface SubmittalReviewerResponse {
  id: string;
  submittal: string;
  reviewer_assignment: string;
  response_code: SubmittalResponseCode;
  comment: string;
  responder_name: string;
  created_at: string;
}

export interface SubmittalStepReviewer {
  id: string;
  reviewer: UserMinimal | null;
  reviewer_role: string;
  is_required: boolean;
  is_optional: boolean;
  due_date: string | null;
  has_responded: boolean;
  responded_at: string | null;
  responses: SubmittalReviewerResponse[];
}

export interface SubmittalWorkflowStep {
  id: string;
  step_number: number;
  title: string;
  is_completed: boolean;
  completed_at: string | null;
  due_date: string | null;
  reviewers: SubmittalStepReviewer[];
}

export interface SubmittalStatusHistoryEntry {
  id: string;
  from_status: SubmittalStatus | '';
  to_status: SubmittalStatus;
  changed_by: UserMinimal | null;
  note: string;
  created_at: string;
}

export interface SubmittalAuditLogEntry {
  id: string;
  action: string;
  actor: UserMinimal | null;
  message: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface SubmittalSummary {
  id: string;
  project: number;
  submittal_number: string;
  revision_number: number;
  title: string;
  submittal_type: SubmittalType;
  specification_section: string;
  responsible_contractor: string;
  package: PackageSummary | null;
  submitted_by: UserMinimal | null;
  created_by: UserMinimal | null;
  submittal_manager: UserMinimal | null;
  trade: TradeReference | null;
  cost_code: CostCodeReference | null;
  priority: SubmittalPriority;
  due_date: string | null;
  required_response_date: string | null;
  status: SubmittalStatus;
  ball_in_court_user: UserMinimal | null;
  current_reviewer: UserMinimal | null;
  is_overdue: boolean;
  days_open: number;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface SubmittalItem extends SubmittalSummary {
  sequence_number: number;
  description: string;
  is_current_revision: boolean;
  superseded_by: string | null;
  discipline: string;
  received_from: string;
  location_reference: { id: number; name: string } | null;
  drawing_reference: { id: string; label: string } | null;
  hotspot: number | null;
  received_date: string | null;
  final_response_code: SubmittalResponseCode | '';
  close_comments: string;
  close_date: string | null;
  distribution_recipients: UserMinimal[];
  attachments: SubmittalAttachment[];
  workflow_steps: SubmittalWorkflowStep[];
  reviewer_responses: SubmittalReviewerResponse[];
  status_history: SubmittalStatusHistoryEntry[];
  audit_logs: SubmittalAuditLogEntry[];
}

export interface CreateSubmittalData {
  project: number;
  package?: string | null;
  title: string;
  description?: string;
  specification_section?: string;
  trade?: string | null;
  cost_code?: string | null;
  discipline?: string;
  submittal_type?: SubmittalType;
  responsible_contractor?: string;
  submitted_by?: number | null;
  received_from?: string;
  submittal_manager?: number | null;
  due_date?: string | null;
  required_response_date?: string | null;
  status?: SubmittalStatus;
  priority?: SubmittalPriority;
}

export type UpdateSubmittalData = Partial<CreateSubmittalData>;

export interface SubmittalFilters {
  status?: SubmittalStatus;
  package_id?: string;
  discipline?: string;
  reviewer_id?: number;
  overdue?: boolean;
  current_revision_only?: boolean;
  start_date?: string;
  end_date?: string;
  search?: string;
}

export interface SubmittalStatusSummaryEntry {
  status: SubmittalStatus;
  count: number;
}

export type SubmittalSortKey =
  | 'submittal_number'
  | 'status'
  | 'priority'
  | 'reviewer'
  | 'required_response_date'
  | 'days_open';

export interface SubmittalSortState {
  key: SubmittalSortKey;
  direction: 'asc' | 'desc';
}

export const SUBMITTAL_STATUS_LABELS: Record<SubmittalStatus, string> = {
  DRAFT: 'Draft',
  OPEN: 'Open',
  IN_REVIEW: 'In Review',
  APPROVED: 'Approved',
  APPROVED_WITH_COMMENTS: 'Approved With Comments',
  REVISE_RESUBMIT: 'Revise & Resubmit',
  REJECTED: 'Rejected',
  CLOSED: 'Closed',
};

export const SUBMITTAL_PRIORITY_LABELS: Record<SubmittalPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export const SUBMITTAL_TYPE_LABELS: Record<SubmittalType, string> = {
  SHOP_DRAWING: 'Shop Drawing',
  PRODUCT_DATA: 'Product Data',
  SAMPLE: 'Sample',
  CALCULATION: 'Calculation',
  BENCHMARK_DOCUMENT: 'Benchmark Document',
  WARRANTY: 'Warranty',
  O_AND_M_MANUAL: 'O&M Manual',
  OTHER: 'Other',
};

export const SUBMITTAL_RESPONSE_LABELS: Record<SubmittalResponseCode, string> = {
  FOR_RECORD_ONLY: 'For Record Only',
  APPROVED: 'Approved',
  APPROVED_AS_NOTED: 'Approved As Noted',
  APPROVED_WITH_COMMENTS: 'Approved With Comments',
  REVISE_RESUBMIT: 'Revise & Resubmit',
  REJECTED: 'Rejected',
  VOID: 'Void',
};
